// ---------------------------------------------------------------------------
// TestSetDetailView — Flat detail view for a single test set (no tabs).
//
// Shows: header with actions, badge row, metadata grid (access, test set ID,
// source dataset, owner, created, modified), and a data entries table with
// Add Data / delete capabilities.
// ---------------------------------------------------------------------------

import {
  Badge,
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Link,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Add16Filled,
  Add16Regular,
  Copy16Filled,
  Copy16Regular,
  Delete16Filled,
  Delete16Regular,
  Dismiss24Regular,
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { TESTSET_ENTRY_PAGE_SIZES } from '../../../constants/index.js';
import { useTestSetEntries } from '../../../hooks/useTestSetEntries/useTestSetEntries.js';
import type { DataEntry } from '../../../services/dataHubService.interface.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';
import { AddTestSetDataDialog } from '../AddTestSetDataDialog/AddTestSetDataDialog.js';

import type { TestSetDetailViewProps } from './TestSetDetailView.interface.js';
import { useStyles } from './TestSetDetailView.style.js';

const AddIcon = bundleIcon(Add16Filled, Add16Regular) as React.FC;
const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const DeleteIcon = bundleIcon(Delete16Filled, Delete16Regular) as React.FC;
const MoreIcon = bundleIcon(
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
) as React.FC;

const COPY_FEEDBACK_MS = 1500;

const STATUS_BADGE_COLOR = {
  Published: 'success',
  Draft: 'important',
  Versioned: 'subtle',
} as const;

function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 12)}...` : id;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Copyable ID field ───────────────────────────────────────────────────────

function CopyableId({ value }: { value: string }) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    });
  }, [value]);

  return (
    <span className={styles.fieldValue}>
      {truncateId(value)}
      <Tooltip content={copied ? 'Copied!' : 'Copy'} relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<CopyIcon />}
          onClick={handleCopy}
          aria-label="Copy ID"
        />
      </Tooltip>
    </span>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function TestSetDetailView({
  testSet,
  service,
  onDeleted,
  onRefetch,
}: TestSetDetailViewProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();
  const navigate = useNavigate();
  const badgeColor = STATUS_BADGE_COLOR[testSet.status] ?? ('subtle' as const);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftDeleteDialogOpen, setDraftDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [showAddDataDialog, setShowAddDataDialog] = useState(false);
  const [pendingDeleteEntry, setPendingDeleteEntry] =
    useState<DataEntry | null>(null);
  const [isDeletingEntry, setIsDeletingEntry] = useState(false);
  const [deleteEntryError, setDeleteEntryError] = useState<string | null>(null);

  const {
    entries,
    totalCount,
    isLoading: isLoadingEntries,
    error: entriesError,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    goToNextPage,
    goToPrevPage,
    setPageSize,
    pageSize,
    refetch: refetchEntries,
  } = useTestSetEntries({
    testSetId: testSet.id,
    version: testSet.version,
  });

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    setPublishError(null);
    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetPublish);
    try {
      await service.publishTestSet(testSet.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setPublishError(
        err instanceof Error ? err.message : 'Failed to publish test set',
      );
    } finally {
      setIsPublishing(false);
    }
  }, [service, testSet.id, onRefetch, scenarioFactory]);

  const handleDemote = useCallback(async () => {
    setIsPublishing(true);
    setPublishError(null);
    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetDemote);
    try {
      await service.createDraftTestSet(testSet.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setPublishError(
        err instanceof Error ? err.message : 'Failed to demote test set',
      );
    } finally {
      setIsPublishing(false);
    }
  }, [service, testSet.id, onRefetch, scenarioFactory]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetDelete);
    const scenarioData = { 'Scenario.DeleteMode': 'soft' };
    try {
      await service.deleteTestSet(testSet.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      onDeleted();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete test set',
      );
      setIsDeleting(false);
    }
  }, [service, testSet.id, onDeleted, scenarioFactory]);

  const handleDeleteDraft = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetDelete);
    const scenarioData = { 'Scenario.DeleteMode': 'draft' };
    try {
      await service.deleteTestSetDraft(testSet.id, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      setDraftDeleteDialogOpen(false);
      setIsDeleting(false);
      onRefetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete draft version',
      );
      setIsDeleting(false);
    }
  }, [service, testSet.id, onRefetch, scenarioFactory]);

  const handleClose = useCallback(() => {
    void navigate('/testsets');
  }, [navigate]);

  const handleOpenSourceDataset = useCallback(() => {
    void navigate(
      `/datasets?datasetId=${encodeURIComponent(testSet.dataSetId)}&tab=data-items`,
    );
  }, [navigate, testSet.dataSetId]);

  const handleAddDataComplete = useCallback(() => {
    refetchEntries();
    onRefetch();
  }, [refetchEntries, onRefetch]);

  const handleDeleteEntry = useCallback(async () => {
    if (pendingDeleteEntry === null) return;
    setIsDeletingEntry(true);
    setDeleteEntryError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.TestsetDeleteData,
    );
    try {
      await service.deleteTestSetData(testSet.id, [pendingDeleteEntry.dataId], {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      setPendingDeleteEntry(null);
      refetchEntries();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setDeleteEntryError(
        err instanceof Error ? err.message : 'Failed to delete entry',
      );
    } finally {
      setIsDeletingEntry(false);
    }
  }, [
    service,
    testSet.id,
    pendingDeleteEntry,
    refetchEntries,
    scenarioFactory,
  ]);

  const rangeStart = totalCount !== null ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd =
    totalCount !== null
      ? Math.min(currentPage * pageSize, totalCount)
      : entries.length;

  return (
    <div className={styles.root}>
      {/* Header with actions */}
      <div className={styles.header}>
        <Text as="span" className={styles.headerTitle}>
          {testSet.name}
        </Text>

        <div className={styles.headerActions}>
          {testSet.status === 'Draft' && (
            <>
              <Button
                appearance="primary"
                size="small"
                icon={<AddIcon />}
                onClick={() => setShowAddDataDialog(true)}
              >
                Add Data
              </Button>
              <Button
                appearance="primary"
                size="small"
                disabled={isPublishing}
                icon={isPublishing ? <Spinner size="tiny" /> : undefined}
                onClick={() => {
                  void handlePublish();
                }}
              >
                Publish
              </Button>
            </>
          )}
          {testSet.status === 'Published' && (
            <Button
              appearance="secondary"
              size="small"
              disabled={isPublishing}
              icon={isPublishing ? <Spinner size="tiny" /> : undefined}
              onClick={() => {
                void handleDemote();
              }}
            >
              Demote to draft
            </Button>
          )}
          {publishError !== null && (
            <Text className={styles.actionError}>{publishError}</Text>
          )}

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<MoreIcon />}
                aria-label="More actions"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {testSet.status === 'Draft' && (
                  <MenuItem
                    data-testid="delete-draft-menu-item"
                    icon={<DeleteIcon />}
                    onClick={() => {
                      setDeleteError(null);
                      setDraftDeleteDialogOpen(true);
                    }}
                  >
                    Delete Draft
                  </MenuItem>
                )}
                <MenuItem
                  icon={<DeleteIcon />}
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteDialogOpen(true);
                  }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular />}
            onClick={handleClose}
            aria-label="Close"
          />
        </div>
      </div>

      {/* Badge row */}
      <div className={styles.badgeRow}>
        <Badge appearance="filled" color={badgeColor}>
          {testSet.status}
        </Badge>
        {testSet.dataType && (
          <Badge appearance="tint" color="brand">
            {testSet.dataType}
          </Badge>
        )}
        <Badge appearance="outline" color="informative">
          v{testSet.version}
        </Badge>
      </div>

      {/* Content area */}
      <div className={styles.content}>
        {/* Metadata grid */}
        <div className={styles.metadataGrid}>
          <div>
            <Text as="span" className={styles.fieldLabel}>
              Access
            </Text>
            <Text as="span" className={styles.fieldValue}>
              <Badge appearance="tint" color="informative">
                {testSet.accessLevel ?? 'Tenant-wide'}
              </Badge>
            </Text>
          </div>

          <div>
            <Text as="span" className={styles.fieldLabel}>
              Test Set ID
            </Text>
            <CopyableId value={testSet.testSetId} />
          </div>

          <div>
            <Text as="span" className={styles.fieldLabel}>
              Source Dataset
            </Text>
            <div className={styles.sourceDatasetRow}>
              <Text as="span" className={styles.fieldValue}>
                {truncateId(testSet.dataSetId)}
              </Text>
              <Link
                className={styles.openLink}
                onClick={handleOpenSourceDataset}
              >
                <Open16Regular /> Open
              </Link>
            </div>
          </div>

          <div>
            <Text as="span" className={styles.fieldLabel}>
              Owner
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {testSet.creatorName ?? '—'}
            </Text>
          </div>

          <div>
            <Text as="span" className={styles.fieldLabel}>
              Created
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {formatDateTime(testSet.createdAt)}
            </Text>
          </div>

          <div>
            <Text as="span" className={styles.fieldLabel}>
              Modified
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {formatDateTime(testSet.updatedAt)}
            </Text>
          </div>
        </div>

        {/* Data entries section */}
        <div className={styles.entriesSection}>
          <div className={styles.entriesHeader}>
            <Text as="span" className={styles.entriesTitle}>
              Data
            </Text>
            {testSet.status === 'Draft' && (
              <Button
                appearance="primary"
                size="small"
                icon={<AddIcon />}
                onClick={() => setShowAddDataDialog(true)}
              >
                Add Data
              </Button>
            )}
          </div>

          {isLoadingEntries ? (
            <div className={styles.loadingState}>
              <Spinner label="Loading entries..." />
            </div>
          ) : entriesError !== null ? (
            <div className={styles.errorState}>
              <Body1>Failed to load entries: {entriesError}</Body1>
            </div>
          ) : entries.length === 0 ? (
            <div className={styles.emptyState}>
              <Body1>No entries found.</Body1>
            </div>
          ) : (
            <Table aria-label="Test set entries">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Data ID</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  {testSet.status === 'Draft' && (
                    <TableHeaderCell aria-label="Actions" />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.name ?? entry.dataId}</TableCell>
                    <TableCell>
                      <CopyableIdCell value={entry.dataId} />
                    </TableCell>
                    <TableCell>{formatDate(entry.createdAt)}</TableCell>
                    {testSet.status === 'Draft' && (
                      <TableCell>
                        <Tooltip content="Delete entry" relationship="label">
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DeleteIcon />}
                            aria-label={`Delete entry ${entry.name ?? entry.dataId}`}
                            onClick={() => setPendingDeleteEntry(entry)}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination footer */}
          {!isLoadingEntries && entriesError === null && entries.length > 0 && (
            <div className={styles.footer}>
              <span className={styles.footerLeft}>
                {totalCount !== null
                  ? `${rangeStart}–${rangeEnd} of ${totalCount} entries`
                  : `${entries.length} entries`}
              </span>

              <div className={styles.footerCenter}>
                <span>Show</span>
                <Select
                  size="small"
                  value={String(pageSize)}
                  onChange={(_, data) => setPageSize(Number(data.value))}
                  aria-label="Page size"
                >
                  {TESTSET_ENTRY_PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
                <Button
                  appearance="subtle"
                  size="small"
                  disabled={!hasPrevPage}
                  onClick={goToPrevPage}
                >
                  Previous
                </Button>
                <span className={styles.footerPageLabel}>
                  Page {currentPage}
                  {totalPages !== null ? ` of ${totalPages}` : ''}
                </span>
                <Button
                  appearance="subtle"
                  size="small"
                  disabled={!hasNextPage}
                  onClick={goToNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(_, data) => {
          setDeleteDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete test set?</DialogTitle>
            <DialogContent>
              <Body1>&ldquo;{testSet.name}&rdquo; will be deleted.</Body1>
              {deleteError !== null && (
                <Body1 className={styles.deleteError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleDelete();
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete draft confirmation dialog */}
      <Dialog
        open={draftDeleteDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open && !isDeleting) setDraftDeleteDialogOpen(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete draft version?</DialogTitle>
            <DialogContent>
              <Body1>
                The draft version of &ldquo;{testSet.name}&rdquo; will be
                permanently deleted, restoring the previously published version.
                If this is the only version, the entire test set will be
                deleted.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.deleteError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                data-testid="delete-draft-confirm-button"
                appearance="primary"
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleDeleteDraft();
                }}
              >
                Delete Draft
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete entry confirmation dialog */}
      <Dialog
        open={pendingDeleteEntry !== null}
        onOpenChange={(_e, data) => {
          if (!data.open && !isDeletingEntry) {
            setPendingDeleteEntry(null);
            setDeleteEntryError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete entry?</DialogTitle>
            <DialogContent>
              <Body1>
                &ldquo;
                {pendingDeleteEntry?.name ?? pendingDeleteEntry?.dataId}
                &rdquo; will be removed from this test set.
              </Body1>
              {deleteEntryError !== null && (
                <Body1 className={styles.dialogError}>{deleteEntryError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                icon={isDeletingEntry ? <Spinner size="tiny" /> : undefined}
                disabled={isDeletingEntry}
                onClick={() => {
                  void handleDeleteEntry();
                }}
              >
                Delete
              </Button>
              <Button
                appearance="secondary"
                disabled={isDeletingEntry}
                onClick={() => {
                  setPendingDeleteEntry(null);
                  setDeleteEntryError(null);
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Add data dialog */}
      <AddTestSetDataDialog
        open={showAddDataDialog}
        testSetId={testSet.id}
        dataSetId={testSet.dataSetId}
        onClose={() => setShowAddDataDialog(false)}
        onAdded={handleAddDataComplete}
      />
    </div>
  );
}

// ── Inline copyable ID for table cells ──────────────────────────────────────

function CopyableIdCell({ value }: { value: string }) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    });
  }, [value]);

  return (
    <div className={styles.dataIdCell}>
      <span>{truncateId(value)}</span>
      <Tooltip
        content={copied ? 'Copied!' : 'Copy data ID'}
        relationship="label"
      >
        <Button
          appearance="subtle"
          size="small"
          icon={<CopyIcon />}
          onClick={handleCopy}
          aria-label="Copy data ID"
        />
      </Tooltip>
    </div>
  );
}
